// app/components/thailand-premium-tapioca-starch-products/TapiocaPearl.tsx
"use client";

import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { renderWithLineBreaks } from "@/app/utils/renderWithLineBreaks";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

export default function TapiocaPearl() {
  const t = useTranslations("OurProducts");
  const locale = useLocale();

  const pagePath = "/thailand-premium-tapioca-starch-products";

  // ---- default จาก i18n / assets (SSR + SEO) ----
  const defaultTitle = t("Pearl.title");
  const defaultContent = t("Pearl.content");
  const defaultSubtitle = t("Pearl.subtitle");
  const defaultImage = "/assets/thailand-premium-tapioca-starch-products/pearl/img1.png";

  // ---- state สำหรับแสดงจริง (จะถูก override ด้วย inline edits ถ้ามี) ----
  const [title, setTitle] = React.useState(defaultTitle);
  const [content, setContent] = React.useState(defaultContent);
  const [subtitle, setSubtitle] = React.useState(defaultSubtitle);
  const [imageSrc, setImageSrc] = React.useState(defaultImage);

  // locale เปลี่ยน → reset เป็น default ก่อน
  React.useEffect(() => {
    setTitle(defaultTitle);
    setContent(defaultContent);
    setSubtitle(defaultSubtitle);
    setImageSrc(defaultImage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

 //
  React.useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          // ใช้ cmsKey ถ้ามี
          if (e.cmsKey) {
            map[e.cmsKey] = e.value;
            return;
          }
          // fallback: หา key จาก selector (กรณี row เก่า)
          const match = e.selector?.match(/data-cms-key="([^"]+)"/);
          if (match) {
            const key = match[1];
            map[key] = e.value;
          }
        });

        // override เฉพาะ key ที่มีใน inline
        if (map["ourproducts.pearl.title"]) {
          setTitle(map["ourproducts.pearl.title"]);
        }
        if (map["ourproducts.pearl.content"]) {
          setContent(map["ourproducts.pearl.content"]);
        }
        if (map["ourproducts.pearl.subtitle"]) {
          setSubtitle(map["ourproducts.pearl.subtitle"]);
        }
        if (map["ourproducts.pearl.image"]) {
          setImageSrc(map["ourproducts.pearl.image"]);
        }
      } catch (err) {
        console.warn("[TapiocaPearl] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  return (
    <Box
      id="tapioca-pearl"
      className="pearl-wrap"
      sx={{
        pt: { xs: 0, md: 2 },
        pb: { xs: 0, md: 0 }
      }}>
      <Container className="container">
        <Grid
          container
          spacing={{ xs: 3, md: 10 }}
          sx={{
            justifyContent: "center",
            alignItems: "center",
            py: 3
          }}>
          <Grid
            size={{ xs: 12, md: 6 }}
            data-aos="fade-right"
            sx={{
              order: { xs: 2, md: 1 }
            }}
          >
            <Image
              src={imageSrc}
              title="thailand tapioca pearl"
              alt="Tapioca pearl from Thailand in standard and custom sizes"
              width={827}
              height={600}
              className="img-fluid"
              data-cms-key="ourproducts.pearl.image"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }} sx={{
            order: { xs: 1, md: 2 }
          }}>
            <Typography
              variant="h2"
              data-aos="fade-up"
              data-cms-key="ourproducts.pearl.title"
              sx={{
                mb: 3,
                whiteSpace: { xs: "normal", lg: "pre-line" }
              }}>
              {renderWithLineBreaks(title)}
            </Typography>

            <Typography
              variant="body1"
              data-aos="fade-up"
              data-cms-key="ourproducts.pearl.content"
              sx={{
                mb: 3
              }}
            >
              {renderWithLineBreaks(content)}
            </Typography>

            <Typography
              variant="h5"
              data-aos="fade-up"
              data-cms-key="ourproducts.pearl.subtitle"
            >
              {renderWithLineBreaks(subtitle)}
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}